import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import { useTheme } from '@mui/material/styles'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useSnackbar } from 'notistack'
import { useContext, useMemo, useState } from 'react'
import { isRouted } from '../../lib/ballad'
import { isAdmin } from '../../lib/user'
import { Ballad } from '@digimap/types'
import DownloadGpxButton from '../../components/DownloadGpxButton'
import LeafletMap from '../../components/leaflet-react/components/LeafletMap'
import LeafletLocateControl from '../../components/leaflet-react/components/LocateControl'
import BalladPublicTransport from '../../components/leaflet/BalladPublicTransport'
import LeafletBalladRoute from '../../components/leaflet/BalladRoute'
import LeafletPOIsControl from '../../components/leaflet/POIsControl'
import { DEFAULT_MAP_OPTIONS } from '../../constants/leaflet'
import { getMapProviders } from '../../constants/leafletMapProviders'
import { getErrorMessage } from '../../lib/errors'
import { toggleBalladCompleted } from '../../services'
import { formatDistance } from '../AdminBalladDetailsPage/lib/format'
import { GpxContext } from '../../state/gpx/GpxProvider'
import { UserContext } from '../../state/user/UserProvider'
import GpxFilesList from '../AdminGpxHistoryPage/components/GpxFilesList'
import { decode } from '@mapbox/polyline'

interface BalladDetailsProps {
  ballad: Ballad
  completed: boolean
  onCompletedChange: (balladId: string, completed: boolean) => void
}

export default function BalladDetails(props: BalladDetailsProps) {
  const { enqueueSnackbar } = useSnackbar()
  const theme = useTheme()
  const breakpointsUp = useMediaQuery(theme.breakpoints.up('md'))
  const userContext = useContext(UserContext)

  const { ballad } = props

  const [hasRoute] = useState(isRouted(ballad))
  const [showRoute, setShowRoute] = useState(hasRoute)
  const [routePoints, setRoutePoints] = useState<[number, number][]>([])

  const [completed, setCompleted] = useState<boolean>(props.completed)

  useMemo(() => {
    if (ballad.track) {
      const points = decode(ballad.track)
      if (points.length >= 2) {
        setRoutePoints(points)
        setShowRoute(true)
      } else {
        enqueueSnackbar('Route has less than 3 points', { variant: 'error' })
      }
    }
  }, [ballad.track, enqueueSnackbar])

  const { gpx: gpxFiles } = useContext(GpxContext)
  const balladGpxFiles = gpxFiles.filter(({ ballads }) => {
    if (!ballads) return false
    return ballads.includes(ballad._id)
  })

  return (
    <Grid
      container
      spacing={0}
      direction={breakpointsUp ? 'row' : 'column'}
      className='full-height-without-toolbar'
    >
      <Grid item>
        <Typography variant='h5' component='h1'>
          {ballad.name}
        </Typography>
        {ballad.distance && (
          <Typography component='p'>
            Distance: {formatDistance(ballad.distance)}
          </Typography>
        )}
        <BalladPublicTransport
          startIds={ballad.publicTransportStartIds}
          endIds={ballad.publicTransportEndIds}
          visible={true}
        />
        {isRouted(ballad) && (
          <div>
            <DownloadGpxButton
              path={routePoints}
              name={ballad.name}
              disabled={!Boolean(routePoints.length)}
            />
          </div>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={completed}
              onChange={async (e) => {
                const newCompleted = e.target.checked
                setCompleted(newCompleted)
                try {
                  props.onCompletedChange(ballad._id, newCompleted)
                  await toggleBalladCompleted(ballad._id, newCompleted)
                  enqueueSnackbar('Ballad completion successfully toggled', {
                    variant: 'success',
                  })
                } catch (e) {
                  enqueueSnackbar(getErrorMessage(e), { variant: 'error' })
                }
              }}
              value='completed'
              color='primary'
            />
          }
          label='Completed'
        />
        {balladGpxFiles && (
          <GpxFilesList gpxFiles={balladGpxFiles} defaultVisibility={false} />
        )}
      </Grid>
      <Grid item style={{ flexGrow: 1, display: 'flex' }}>
        <LeafletMap
          opts={DEFAULT_MAP_OPTIONS}
          style={{ flex: '1', height: 'auto' }}
          mapProviders={getMapProviders(isAdmin(userContext))}
        />
        {showRoute && (
          <LeafletBalladRoute
            latlngs={routePoints}
            options={{
              color: '#FF5722',
            }}
          />
        )}
        <LeafletPOIsControl />
        <LeafletLocateControl
          options={{
            position: 'topleft',
            setView: false,
            locateOptions: {
              maxZoom: 16,
              enableHighAccuracy: true,
            },
          }}
        />
      </Grid>
    </Grid>
  )
}

BalladDetails.displayName = 'BalladDetails'
